import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Field, Label as HeadlessLabel } from '@headlessui/react'
import { Button } from '@/app/common/components/button/Button'
import Checkbox from '@/app/common/components/Checkbox'
import Label from '@/app/common/components/Label'
import SearchInput from '@/app/common/components/SearchInput'
import SimpleSelector from '@/app/common/components/SimpleSelector'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { ChevronDownIcon } from '@/app/common/icons/arrows'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { BankAccountQueries } from '@/queries/bank_accounts/queries'
import { PURSE_KEYS } from '@/queries/program_purses/keys'
import type { Purse } from '@/queries/program_purses/schemas'
import { useBulkAssignPurseBankAccount } from '@/queries/program_purses/useBulkAssignPurseBankAccount'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  programId: number
  purses: Purse[]
}

export default function BulkAssignBankAccountDialog({
  open,
  onOpenChange,
  programId,
  purses,
}: Props) {
  const mutation = useBulkAssignPurseBankAccount()
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && mutation.status === 'pending') {
      return
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Mounted only while open so the selection resets every time the dialog is reopened. */}
      {open && (
        <BulkAssignBankAccountDialogContent
          onOpenChange={onOpenChange}
          programId={programId}
          purses={purses}
          mutation={mutation}
        />
      )}
    </Dialog>
  )
}

type ContentProps = Omit<Props, 'open'> & {
  mutation: ReturnType<typeof useBulkAssignPurseBankAccount>
}

function BulkAssignBankAccountDialogContent({
  onOpenChange,
  programId,
  purses,
  mutation,
}: ContentProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<
    number | null
  >(null)
  const [selectedPurseIds, setSelectedPurseIds] = useState<number[]>([])

  const bankAccountsQuery = useQuery(
    BankAccountQueries.getAllByProgramId(programId)
  )
  const bankAccounts = bankAccountsQuery.data ?? []
  const bankAccountOptions = bankAccounts.map((ba) => ({
    id: ba.id,
    name: ba.name,
  }))

  const isPending = mutation.status === 'pending'
  const isLoadingBankAccounts = bankAccountsQuery.status === 'pending'
  const hasNoBankAccounts =
    bankAccountsQuery.status === 'success' && bankAccountOptions.length === 0

  const getBankAccountName = (bankAccountId: number | null | undefined) => {
    if (bankAccountId === undefined) return 'Unknown'
    if (bankAccountId === null) return 'No bank account'
    return (
      bankAccounts.find((ba) => ba.id === bankAccountId)?.name ??
      `Account #${bankAccountId}`
    )
  }

  const searchQuery = search.trim().toLowerCase()
  const filteredPurses = purses.filter((purse) => {
    if (searchQuery.length === 0) return true
    return (
      purse.name.toLowerCase().includes(searchQuery) ||
      String(purse.id).includes(searchQuery)
    )
  })

  const allFilteredSelected =
    filteredPurses.length > 0 &&
    filteredPurses.every((purse) => selectedPurseIds.includes(purse.id))

  const togglePurse = (purseId: number) => {
    setSelectedPurseIds((current) =>
      current.includes(purseId)
        ? current.filter((id) => id !== purseId)
        : [...current, purseId]
    )
  }

  // Acts on the visible (filtered) purses only, keeping selections hidden by the search intact.
  const toggleAllFiltered = () => {
    const filteredIds = filteredPurses.map((purse) => purse.id)
    setSelectedPurseIds((current) =>
      allFilteredSelected
        ? current.filter((id) => !filteredIds.includes(id))
        : Array.from(new Set([...current, ...filteredIds]))
    )
  }

  const handleAssign = () => {
    if (selectedBankAccountId == null || selectedPurseIds.length === 0) return

    mutation.mutate(
      {
        program_id: programId,
        bank_account_id: selectedBankAccountId,
        program_purse_ids: selectedPurseIds,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSES(programId),
          })
          // Prefix match: refreshes the detail view of every purse that may have changed.
          queryClient.invalidateQueries({ queryKey: ['PURSE_DETAILS'] })
          toast({
            title: 'Success',
            description: `Bank account assigned to ${selectedPurseIds.length} ${
              selectedPurseIds.length === 1 ? 'purse' : 'purses'
            }`,
          })
          onOpenChange(false)
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive',
          })
        },
      }
    )
  }

  return (
    <DialogContent
      className="max-w-2xl"
      onInteractOutside={(event) => {
        if (isPending) {
          event.preventDefault()
        }
      }}
    >
      <DialogHeader>
        <DialogTitle>Bulk Assign Bank Account</DialogTitle>
        <DialogDescription className="text-left">
          Select a bank account and the purses whose distributions should use
          it. Any bank account currently assigned to the selected purses will be
          replaced.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Bank Account</Label>
          <SimpleSelector
            list={bankAccountOptions}
            selected={
              bankAccountOptions.find(
                (option) => option.id === selectedBankAccountId
              ) ?? null
            }
            setSelected={(option) => setSelectedBankAccountId(option.id)}
            icon={<ChevronDownIcon className="text-lg" />}
            placeholder={
              isLoadingBankAccounts
                ? 'Loading bank accounts...'
                : hasNoBankAccounts
                  ? 'No bank accounts available'
                  : 'Select a bank account'
            }
            isDisabled={
              isLoadingBankAccounts ||
              hasNoBankAccounts ||
              bankAccountsQuery.status === 'error' ||
              isPending
            }
          />
          {bankAccountsQuery.status === 'error' && (
            <p className="text-sm text-red-600">
              {bankAccountsQuery.error.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-end justify-between">
            <Label>Purses</Label>
            <span className="text-sm text-gray-600">
              {selectedPurseIds.length} of {purses.length} selected
            </span>
          </div>

          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search purses"
            disabled={isPending}
          />

          <div className="max-h-80 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-200">
            {filteredPurses.length === 0 ? (
              <p className="p-4 text-sm text-gray-600">No purses found.</p>
            ) : (
              <>
                {/* The checkbox sits beside the Label, not inside it, so a click toggles it exactly once. */}
                <Field
                  disabled={isPending}
                  className="flex items-center gap-3 bg-gray-50 pl-4"
                >
                  <Checkbox
                    name="bulk-assign-select-all"
                    checked={allFilteredSelected}
                    onChange={toggleAllFiltered}
                    disabled={isPending}
                  />
                  <HeadlessLabel className="flex-1 cursor-pointer py-3 pr-4 text-sm font-medium text-gray-900 data-[disabled]:cursor-default">
                    Select all
                    {searchQuery.length > 0 && ' matching purses'}
                  </HeadlessLabel>
                </Field>

                {filteredPurses.map((purse) => (
                  <Field
                    key={purse.id}
                    disabled={isPending}
                    className="flex items-center gap-3 pl-4 hover:bg-gray-50"
                  >
                    <Checkbox
                      name={`bulk-assign-purse-${purse.id}`}
                      checked={selectedPurseIds.includes(purse.id)}
                      onChange={() => togglePurse(purse.id)}
                      disabled={isPending}
                    />
                    <HeadlessLabel className="block min-w-0 flex-1 cursor-pointer py-3 pr-4 data-[disabled]:cursor-default">
                      <span className="block truncate text-sm font-medium text-gray-900">
                        {purse.name}
                      </span>
                      <span className="block text-xs text-gray-500">
                        PURSE ID: {purse.id} · Current:{' '}
                        {getBankAccountName(purse.bank_account_id)}
                      </span>
                    </HeadlessLabel>
                  </Field>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      <DialogFooter className="gap-2 sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isPending}
        >
          Cancel
        </Button>
        <div>
          <SubmitButton
            type="button"
            buttonTxt="Assign Bank Account"
            submittingText="Assigning..."
            status={mutation.status}
            forceDisabled={
              selectedBankAccountId == null || selectedPurseIds.length === 0
            }
            onClick={handleAssign}
          />
        </div>
      </DialogFooter>
    </DialogContent>
  )
}
